import { DestroyRef, Injectable, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  distinctUntilChanged,
  filter,
  map
} from 'rxjs';
import * as SocketIo from 'socket.io-client';

import { environment } from '@gfe/../environments/environment';
import { GbeTokensService } from '@gfe/core/services/gbe-tokens/gbe-tokens.service';

import { RootState } from '@core/models/auth/auth.model';
import {
  CODED_MESSAGE_VERBATIM_KEYS,
  CodedStatusMessage
} from '@core/models/coded-status-message.model';
import { Invitation } from '@core/models/invitation/invitation.model';
import { NotificationType } from '@core/models/notifications.model';
import { JobStatusV4, OptimizationResult } from '@core/models/optimization.model';
import { DeployStatusCode } from '@core/models/plan.model';
import { NotificationService } from '@core/services/notification/notification.service';
import { SoundService } from '@core/services/sound/sound.service';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';
import { InvitationsActions } from '@core/store/invitations/invitations.actions';
import { PlanEditActions } from '@core/store/plan-edit/plan-edit.actions';
import * as PlanActions from '@core/store/plan/plan.actions';
import { selectNotifications } from '@core/store/user-preferences/user-preferences.selectors';
import { convertKeysToCamelCase } from '@core/utils/case-conversions';
import { isNil } from '@core/utils/shared-utils';

export interface GBELog {
  level: 'info' | 'warning' | 'error' | 'debug';
  message: string;
  source: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

type EventHandler<T = unknown> = (data: T) => void;

@Injectable({
  providedIn: 'root'
})
export class WebsocketService implements OnDestroy {
  private readonly apiWebSocketUrl = environment.gbeWebSocketUrl;
  private readonly store = inject(Store<RootState>);
  private readonly tokensService = inject(GbeTokensService);
  private readonly notificationsService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly soundService = inject(SoundService);
  private readonly notificationPreferences = this.store.selectSignal(selectNotifications);
  private translate = inject(TranslateService);

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private retryAttempts = 0;
  private readonly maxRetries = 10000;
  private readonly retryDelay = 2000;

  socket!: SocketIo.Socket;
  connected$ = new BehaviorSubject<boolean>(false);

  private shouldBeConnected = false;
  private isConnecting = false;
  private connectionState: 'disconnected' | 'connecting' | 'connected' | 'error' =
    'disconnected';
  private registeredHandlers = new Map<string, Array<EventHandler>>();

  // Track initialization state to prevent double connections
  private isInitialized = false;
  private defaultHandlersInitialized = false;
  private optimizationsPageActive = false;
  private currentUserId: number | null = null;
  private currentDataspaceId: number | null = null;
  /** Jobs already announced as in progress: SENT then RUNNING is one run starting. */
  private readonly inProgressToastedJobIds = new Set<string>();

  /**
   * Initialize the WebSocket connection for a specific user. Should be called after
   * successful authentication. Idempotent - safe to call multiple times for the same user.
   */
  initialize(userId: number): void {
    console.log(`Initializing WebSocket connection for user: ${userId}`);

    // Check if already initialized for this user
    if (this.isInitialized && this.currentUserId === userId && this.isConnected()) {
      console.log(`WebSocket already initialized for user ${userId}`);
      return;
    }

    // If different user, shutdown existing connection first
    if (this.currentUserId && this.currentUserId !== userId) {
      console.log(
        `User changed from ${this.currentUserId} to ${userId}, shutting down existing connection`
      );
      this.shutdown();
    }

    const accessToken = this.tokensService.getAccessToken();
    if (accessToken) {
      this.currentUserId = userId;
      this.isInitialized = true;
      this.shouldBeConnected = true;
      this.connect(accessToken);
    } else {
      console.warn('No access token available for WebSocket connection');
    }
  }

  /**
   * Shutdown the WebSocket connection and cleanup resources. Should be called on logout or
   * app shutdown.
   */
  shutdown(): void {
    console.log(`Shutting down WebSocket connection for user: ${this.currentUserId}`);
    this.shouldBeConnected = false;
    this.isInitialized = false;
    this.currentUserId = null;
    this.cleanup();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * The server drops a socket's rooms on disconnect, so the join is remembered and re-sent on
   * every connect until the page is left.
   */
  joinOptimizationsRooms(): void {
    this.optimizationsPageActive = true;
    if (this.socket?.connected) {
      this.socket.emit('join-optimizations-rooms');
    }
  }

  leaveOptimizationsPage(): void {
    this.optimizationsPageActive = false;
    if (this.socket?.connected) {
      this.socket.emit('leave-optimizations-rooms');
    }
  }

  private joinDataspaceRoom(dataspaceId: number): void {
    // Record the target room first so a later 'connect' event can (re)join it,
    // even when this is called before the socket has finished connecting.
    this.currentDataspaceId = dataspaceId;
    if (!this.socket?.connected) return;
    this.socket.emit('join-dataspace-room', { dataSpaceId: dataspaceId });
  }

  private leaveCurrentDataspaceRoom(): void {
    if (!this.socket?.connected || this.currentDataspaceId === null) return;
    this.socket.emit('leave-dataspace-room', { dataSpaceId: this.currentDataspaceId });
    this.currentDataspaceId = null;
  }

  private setupDataspaceRoomSubscription(): void {
    const sub = this.store
      .select(selectDataspaceModel)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((ds) => !isNil(ds)),
        map((ds) => ds!.id),
        distinctUntilChanged()
      )
      .subscribe((dataspaceId) => {
        if (this.currentDataspaceId !== null) {
          this.leaveCurrentDataspaceRoom();
        }
        this.joinDataspaceRoom(dataspaceId);
      });
    this.subscriptions.push(sub);
  }

  private cleanup(): void {
    this.defaultHandlersInitialized = false;
    this.currentDataspaceId = null;
    this.shouldBeConnected = false;

    // Cleanup subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    // Cleanup socket
    if (this.socket) {
      this.socket.disconnect();
      this.socket.removeAllListeners();
    }

    // Complete observables
    this.destroy$.next();
    this.destroy$.complete();
    this.connected$.complete();
  }

  connect(accessToken: string): void {
    this.shouldBeConnected = true;

    // Enhanced guard: Prevent multiple connection attempts
    if (this.isConnecting) {
      console.log('Connection already in progress, skipping');
      return;
    }

    if (this.socket?.connected) {
      console.log('Socket already connected, skipping');
      return;
    }

    // Clean up existing socket if present but not connected
    if (this.socket && !this.socket.connected) {
      console.log('Cleaning up existing disconnected socket');
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.isConnecting = true;
    this.connectionState = 'connecting';

    this.socket = SocketIo.io(this.apiWebSocketUrl, {
      auth: {
        token: accessToken
      },
      // Transport socket must be set to 'websocket' to avoid
      // multiple http-polling connections when multiple GBE replicas
      // are running behind the load-balancer.
      transports: ['websocket']
    });

    this.setupEventListeners();
    this.setupDefaultMessageHandlers();
  }

  private setupEventListeners(): void {
    this.socket.on('connect', () => {
      this.isConnecting = false;
      this.connectionState = 'connected';
      this.retryAttempts = 0; // Reset retry counter on successful connection
      this.connected$.next(true);
      if (this.currentDataspaceId !== null) {
        this.socket.emit('join-dataspace-room', { dataSpaceId: this.currentDataspaceId });
      }
      if (this.optimizationsPageActive) {
        this.socket.emit('join-optimizations-rooms');
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('Disconnected from WebSocket server:', reason);
      this.isConnecting = false;
      this.connectionState = 'disconnected';
      this.connected$.next(false);
    });

    this.socket.on('connect_error', (error: Error) => {
      this.isConnecting = false;
      this.connectionState = 'error';
      this.connected$.next(false);

      // Attempt retry if under max retry limit
      this.handleConnectionError(error);
    });
  }

  private setupDefaultMessageHandlers(): void {
    // The store subscription is NOT tied to the socket instance; it must be set
    // up only once, or each reconnect would stack a duplicate (causing duplicate
    // join/leave-dataspace-room emits and duplicate deployStatusUpdated dispatches).
    if (!this.defaultHandlersInitialized) {
      this.defaultHandlersInitialized = true;
      this.setupDataspaceRoomSubscription();
    }

    // Socket-bound listeners MUST re-bind on every connect(): a manual connect_error
    // retry creates a brand-new this.socket, and these must attach to it.
    const deploySubscription = this.listen<Record<string, unknown>>('deploy-plan:status')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((raw) => {
        // GBE emits snake_case (plan_id/config_id); convert before dispatch. This
        // transport bypasses `apiResponseCaseInterceptor`, so it owes the coded
        // messages below the same `details` exemption the interceptor gives them.
        const payload = convertKeysToCamelCase(raw, CODED_MESSAGE_VERBATIM_KEYS) as {
          planId: string;
          configId: string;
          name?: string;
          status: DeployStatusCode;
          updatedAt?: number | string;
          warnings?: CodedStatusMessage[];
          error?: CodedStatusMessage | null;
          totalMessages?: number;
          sentCount?: number;
          failedCount?: number;
        };
        // Prefer the server-provided event time so consumers' staleness guards
        // compare against a single clock; fall back to local time if absent.
        const parsed =
          payload.updatedAt !== undefined ? new Date(payload.updatedAt).getTime() : NaN;
        // Fall back to local time if the server value is absent or unparseable (NaN),
        // so staleness comparisons never receive NaN.
        const updatedAt = Number.isNaN(parsed) ? Date.now() : parsed;
        this.store.dispatch(
          PlanActions.deployStatusUpdated({
            planId: payload.planId,
            configId: payload.configId,
            status: payload.status,
            updatedAt,
            // Real channel name from the server, so the tooltip shows it instead
            // of the configId fallback when the config isn't yet in the plan.
            name: payload.name,
            // Carry the live detail so the error panel / deploy-page error block
            // update without waiting for a REST refetch. Reducer clears the opposite
            // field per status (PROCESSING clears both; SUCCESS→warnings; ERROR→error).
            warnings: payload.warnings,
            error: payload.error,
            // How much actually went out, so the channel shows "73 of 111 sent" the moment
            // the deploy settles. Only the terminal event carries these.
            totalMessages: payload.totalMessages,
            sentCount: payload.sentCount,
            failedCount: payload.failedCount
          })
        );
      });
    this.subscriptions.push(deploySubscription);

    const subscription = this.listen<OptimizationResult>('job-status-updated')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        // Same `details` exemption the deploy channel gets: this payload's `errors[]`
        // carry DataLayer's own payload keys, matched against its JSON Schema.
        const convertedResult = convertKeysToCamelCase(result, CODED_MESSAGE_VERBATIM_KEYS);
        this.dispatchNotification(
          result.status,
          convertedResult.jobId,
          convertedResult.outputPlanId
        );
        // Dispatch plan store action
        this.store.dispatch(PlanActions.optimizationUpdated({ result: convertedResult }));
      });
    this.subscriptions.push(subscription);

    // The two data-space channels below sync a shared view; the private ones above notify the
    // one user an event is about. Nothing here may raise a toast: a viewer who did not launch
    // the job, and did not act on it, has not asked to hear about it.
    //
    // One client can receive the same transition more than once: the optimizations history
    // rooms include the job owner, who also gets the private event, and an unrestricted user
    // with the data space open is in both its room and the global one. Applying a status patch
    // twice is harmless, and only the private channel raises a toast.
    //
    // For a job in an internal planning space GBE sends `optimization:updated` only to the
    // global optimizations room, so beyond the user who launched it, only unrestricted users on
    // the history page stay live; any other viewer refreshes manually.
    const optimizationUpdatedSubscription = this.listen<OptimizationResult>(
      'optimization:updated'
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.store.dispatch(
          PlanActions.optimizationUpdated({
            result: convertKeysToCamelCase(result, CODED_MESSAGE_VERBATIM_KEYS)
          })
        );
      });
    this.subscriptions.push(optimizationUpdatedSubscription);

    // GBE sends it to the whole data-space room, the saver included; the refresh effect drops the
    // copy of a save this tab already applied.
    const planUpdatedSubscription = this.listen<Record<string, unknown>>('plan:updated')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((raw) => {
        const payload = convertKeysToCamelCase(raw) as {
          planId: string;
          planDataRevision: string;
        };
        this.store.dispatch(
          PlanEditActions.planUpdatedRemotely({
            planId: payload.planId,
            planDataRevision: payload.planDataRevision
          })
        );
      });
    this.subscriptions.push(planUpdatedSubscription);

    const optimizationDeletedSubscription = this.listen<Record<string, unknown>>(
      'optimization:deleted'
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((raw) => {
        const payload = convertKeysToCamelCase(raw) as {
          jobId: string;
          planId: string;
        };
        this.store.dispatch(
          PlanActions.optimizationDeleted({
            jobId: payload.jobId,
            planId: payload.planId
          })
        );
      });
    this.subscriptions.push(optimizationDeletedSubscription);

    // Registered here rather than via listen() from the list page: only default handlers are
    // re-bound when a connect_error retry replaces the socket.
    const invitationStatusSubscription = this.listen<Record<string, unknown>>(
      'invitation-status'
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((raw) => {
        this.store.dispatch(
          InvitationsActions.statusReceived({
            invitation: convertKeysToCamelCase(raw) as Invitation
          })
        );
      });
    this.subscriptions.push(invitationStatusSubscription);

    this.socket.onAny((eventName: string, data: unknown) => {
      const handlers = this.registeredHandlers.get(eventName);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(data);
          } catch (error) {
            console.error(`Error in the handler for event ${eventName}:`, error);
          }
        });
      }
    });
  }

  disconnect(): void {
    this.shouldBeConnected = false;
    this.isConnecting = false;
    this.connectionState = 'disconnected';

    if (this.socket) {
      this.socket.disconnect();
      this.connected$.next(false);
    }
  }
  listen<T = unknown>(event: string): Observable<T> {
    return new Observable((observer) => {
      if (!this.socket) {
        observer.error(new Error('Socket not initialized'));
        return;
      }

      const handler = (data: T) => {
        try {
          observer.next(data);
        } catch (error) {
          console.error(`Error processing event ${event}:`, error);
          observer.error(error);
        }
      };

      this.socket.on(event, handler);

      return () => {
        if (this.socket) {
          this.socket.off(event, handler);
        }
      };
    });
  }

  private handleConnectionError(error: Error): void {
    console.error('Error in websocket connection:', error);
    if (this.retryAttempts < this.maxRetries && this.shouldBeConnected) {
      this.retryAttempts++;
      console.log(
        `Retrying connection (${this.retryAttempts}/${this.maxRetries}) in ${this.retryDelay}ms...`
      );

      setTimeout(() => {
        const accessToken = this.tokensService.getAccessToken();
        if (accessToken && this.shouldBeConnected) {
          this.connect(accessToken);
        }
      }, this.retryDelay);
    } else {
      console.error('Max connection retries reached or connection disabled');
    }
  }

  getConnectionState(): string {
    return this.connectionState;
  }

  private dispatchNotification(
    status: JobStatusV4,
    jobId: string | undefined,
    outputPlanId: string | undefined
  ): void {
    // CREATED is announced the instant the job exists, before the user could want an update.
    if (status === JobStatusV4.CREATED) return;

    const inProgress = status === JobStatusV4.SENT || status === JobStatusV4.RUNNING;
    // RETRY leads back to RUNNING within the same run, so it keeps the job's entry too.
    if (jobId && !inProgress && status !== JobStatusV4.RETRY) {
      this.inProgressToastedJobIds.delete(jobId);
    }

    const preferences = this.notificationPreferences();

    if (status === JobStatusV4.OPTIMIZATION_FINISHED) {
      if (!preferences.solverCompleted) return;
      this.notificationsService.show({
        type: NotificationType.SUCCESS,
        title: this.translate.instant('optimization.end.success'),
        message: this.translate.instant('optimization.message.view_results'),
        duration: 5000,
        actions: [
          {
            label: this.translate.instant('optimization.message.view_results'),
            primary: true,
            action: () => {
              if (outputPlanId) {
                const currentUrl = this.router.url;
                const newUrl =
                  currentUrl.substring(0, currentUrl.lastIndexOf('/') + 1) + outputPlanId;
                this.router.navigateByUrl(newUrl);
              }
            }
          }
        ]
      });
      this.soundService.play('success');
    } else if (inProgress) {
      if (jobId) {
        if (this.inProgressToastedJobIds.has(jobId)) return;
        this.inProgressToastedJobIds.add(jobId);
      }
      this.notificationsService.info(
        this.translate.instant('optimization.message.in_progress'),
        '',
        5000
      );
    } else if (status === JobStatusV4.BLOCKED) {
      this.notificationsService.warning(
        this.translate.instant('optimization.end.blocked'),
        this.translate.instant('optimization.message.blocked'),
        8000
      );
    } else if (status === JobStatusV4.CANCELED) {
      // The user asked for this, so it is not a failure — without this branch it falls
      // through to the generic error toast below.
      this.notificationsService.info(
        this.translate.instant('optimization.end.canceled'),
        '',
        5000
      );
    } else if (status === JobStatusV4.ERROR) {
      if (!preferences.solverError) return;
      this.notificationsService.error(
        this.translate.instant('optimization.end.error'),
        '',
        []
      );
    } else {
      if (!preferences.solverError) return;
      this.notificationsService.error(
        this.translate.instant('optimization.message.error'),
        '',
        []
      );
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}
